import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  ListChecks,
  Clock,
  History,
  User,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  Plus,
  Bell,
  Lock,
  CheckCircle,
  Mail,
  BarChart2,
  Heart,
  Users,
  Settings,
  Shield,
  Award
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Custom CSS for sidebar scrolling
const sidebarStyles = `
  .sidebar-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
  }
  
  .sidebar-scroll::-webkit-scrollbar {
    width: 4px;
  }
  
  .sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .sidebar-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 2px;
  }
  
  .sidebar-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.7);
  }
  
  @media (max-width: 768px) {
    .sidebar-scroll {
      scrollbar-width: none;
    }
    
    .sidebar-scroll::-webkit-scrollbar {
      display: none;
    }
  }
`;

const DashboardLayout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 1024);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);
  const [showRoleConfirm, setShowRoleConfirm] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, switchRole } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleRoleSwitch = async (newRole) => {
    setPendingRole(newRole);
    setShowRoleConfirm(true);
  };

  const confirmRoleSwitch = async () => {
    try {
      await switchRole(pendingRole);
      if (pendingRole === 'requestor') {
        navigate('/requester-dashboard');
      } else {
        navigate('/dashboard');
      }
      setShowRoleConfirm(false);
      setPendingRole(null);
      setIsDashboardDropdownOpen(false);
      setIsMobileOpen(false);
    } catch (error) {
      console.error('Failed to switch role:', error);
      setShowRoleConfirm(false);
      setPendingRole(null);
    }
  };

  const cancelRoleSwitch = () => {
    setShowRoleConfirm(false);
    setPendingRole(null);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isAuthenticated) {
      // Determine which dashboard to navigate to based on current location and user role
      const userRole = user?.role || 'donor';
      if (location.pathname.startsWith('/requester-dashboard')) {
        navigate('/requester-dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const isActivePath = (path) => location.pathname === path;

  const renderMenuItem = (item) => {
    if (item.isDivider) {
      return <div key="divider" className="my-3 border-t border-gray-200/50" />;
    }

    if (item.customComponent) {
      // Hide toggle switch when sidebar is collapsed
      if (!isOpen) {
        return null;
      }
      return <item.customComponent key="custom-component" />;
    }

    if (item.isDropdown) {
      return (
        <div className="relative" key={item.label}>
          <button
            onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
            className={`w-full flex items-center justify-between ${isOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5'} rounded-xl transition-all duration-300 group ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 shadow-sm'
                : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
              }`}
          >
            <div className="flex items-center">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                  ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-gray-200 group-hover:to-gray-300'
                }`}>
                <item.icon className={`w-3.5 h-3.5 transition-all duration-300 ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                    ? 'text-white'
                    : 'text-gray-600 group-hover:text-gray-700'
                  }`} />
              </div>
              {isOpen && (
                <div className="ml-3 flex-1 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold">{item.label}</span>
                    <p className="text-[10px] text-gray-500 mt-0.5">Switch between roles</p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${isDashboardDropdownOpen ? 'rotate-180' : ''
                      }`}
                  />
                </div>
              )}
            </div>
          </button>
          {isDashboardDropdownOpen && isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-10 overflow-hidden"
            >
              {item.options.map((option) => (
                <button
                  key={option.path}
                  onClick={() => handleRoleSwitch(option.role)}
                  className={`block w-full text-left px-3 py-2.5 text-sm transition-all duration-200 ${isActivePath(option.path)
                      ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 font-semibold'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-700'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </motion.div>
          )}
          {/* Tooltip for collapsed state */}
          {!isOpen && (
            <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 whitespace-nowrap">
              {item.label}
              <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-0 h-0 border-l-4 border-r-0 border-t-4 border-b-4 border-transparent border-r-gray-900"></div>
            </div>
          )}
        </div>
      );
    }

    if (item.onClick) {
      return (
        <div className="relative group" key={item.label}>
          <button
            onClick={item.onClick}
            className={`w-full flex items-center ${isOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5'} rounded-xl transition-all duration-300 ${item.className || 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
              }`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${item.className?.includes('text-red-600') ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg' : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-gray-200 group-hover:to-gray-300'
              }`}>
              <item.icon className={`w-3.5 h-3.5 transition-all duration-300 ${item.className?.includes('text-red-600') ? 'text-white' : 'text-gray-600 group-hover:text-gray-700'
                }`} />
            </div>
            {isOpen && (
              <div className="ml-2.5">
                <p className="text-xs font-semibold">{item.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
              </div>
            )}
          </button>
          {/* Tooltip for collapsed state */}
          {!isOpen && (
            <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 whitespace-nowrap">
              {item.label}
              <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-0 h-0 border-l-4 border-r-0 border-t-4 border-b-4 border-transparent border-r-gray-900"></div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative group" key={item.path}>
        <Link
          to={item.path}
          className={`flex items-center ${isOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5'} rounded-xl transition-all duration-300 ${isActivePath(item.path)
              ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 shadow-sm'
              : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
            }`}
        >
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${isActivePath(item.path) ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg' : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-gray-200 group-hover:to-gray-300'
            }`}>
            <item.icon className={`w-3.5 h-3.5 transition-all duration-300 ${isActivePath(item.path) ? 'text-white' : 'text-gray-600 group-hover:text-gray-700'
              }`} />
          </div>
          {isOpen && (
            <div className="ml-2.5">
              <p className="text-xs font-semibold">{item.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
            </div>
          )}
        </Link>
        {/* Tooltip for collapsed state */}
        {!isOpen && (
          <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 whitespace-nowrap">
            {item.label}
            <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-0 h-0 border-l-4 border-r-0 border-t-4 border-b-4 border-transparent border-r-gray-900"></div>
          </div>
        )}
      </div>
    );
  };

  // Mobile version of the dashboard toggle
  const MobileDashboardToggle = () => {
    const isDonorDashboard = location.pathname.startsWith('/dashboard');
    const hasRequesterRole = user?.roles?.includes('requestor');
    const hasDonorRole = user?.roles?.includes('donor');

    // If user doesn't have both roles, don't show the toggle
    if (!hasRequesterRole || !hasDonorRole) {
      return null;
    }

    const handleToggle = async () => {
      const newRole = isDonorDashboard ? 'requestor' : 'donor';
      handleRoleSwitch(newRole);
    };

    return (
      <motion.div
        className="px-4 py-3 mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200 shadow-sm">
          <div className="text-center mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-bold text-gray-800 mb-1">Switch Dashboard</p>
            <p className="text-xs text-gray-600">Choose your role</p>
          </div>

          <div className="relative bg-gray-200 rounded-xl p-1">
            <div className="w-full flex items-center justify-between bg-white rounded-lg p-1 relative shadow-sm">
              {/* Donor side */}
              <motion.button
                onClick={isDonorDashboard ? null : handleToggle}
                className={`flex-1 py-2 px-3 rounded-md text-center text-sm font-semibold z-10 transition-all duration-300 flex items-center justify-center space-x-2 ${isDonorDashboard
                    ? 'text-red-600 bg-gradient-to-r from-red-50 to-red-100 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                whileHover={!isDonorDashboard ? { scale: 1.02 } : {}}
                whileTap={!isDonorDashboard ? { scale: 0.98 } : {}}
              >
                <Heart className={`w-4 h-4 ${isDonorDashboard ? 'text-red-600' : 'text-gray-400'}`} />
                <span>Donor</span>
              </motion.button>

              {/* Requester side */}
              <motion.button
                onClick={isDonorDashboard ? handleToggle : null}
                className={`flex-1 py-2 px-3 rounded-md text-center text-sm font-semibold z-10 transition-all duration-300 flex items-center justify-center space-x-2 ${!isDonorDashboard
                    ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                whileHover={isDonorDashboard ? { scale: 1.02 } : {}}
                whileTap={isDonorDashboard ? { scale: 0.98 } : {}}
              >
                <Users className={`w-4 h-4 ${!isDonorDashboard ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>Requester</span>
              </motion.button>

              {/* Sliding background */}
              <motion.div
                className={`absolute top-1 bottom-1 w-1/2 rounded-md bg-gradient-to-r transition-all duration-300 ${isDonorDashboard
                    ? 'from-red-100 to-red-200 translate-x-0'
                    : 'from-blue-100 to-blue-200 translate-x-full'
                  }`}
                layout
              />
            </div>
          </div>

          <div className="mt-3 text-center">
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-2 py-1 shadow-sm">
              <div className={`w-1.5 h-1.5 rounded-full ${isDonorDashboard ? 'bg-red-500' : 'bg-blue-500'}`}></div>
              <p className="text-xs text-gray-600">
                Currently: <span className={`font-bold ${isDonorDashboard ? 'text-red-600' : 'text-blue-600'}`}>
                  {isDonorDashboard ? 'Donor Mode' : 'Requester Mode'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderMobileMenuItem = (item) => {
    if (item.isDivider) {
      return <div key="divider" className="my-3 border-t border-gray-200" />;
    }

    if (item.customComponent) {
      return <MobileDashboardToggle key="mobile-custom-component" />;
    }

    if (item.isDropdown) {
      return (
        <div className="relative" key={item.label}>
          <button
            onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                ? 'text-red-600 bg-gradient-to-r from-red-50 to-red-100'
                : 'text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100'
              }`}
          >
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                  ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200'
                }`}>
                <item.icon className={`w-5 h-5 ${isActivePath('/dashboard') || isActivePath('/requester-dashboard')
                    ? 'text-white'
                    : 'text-gray-600'
                  }`} />
              </div>
              <span className="ml-3">{item.label}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${isDashboardDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {isDashboardDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl mt-2 overflow-hidden"
            >
              {item.options.map((option) => (
                <button
                  key={option.path}
                  onClick={() => handleRoleSwitch(option.role)}
                  className={`block w-full text-left px-8 py-3 text-sm font-semibold transition-all duration-300 ${isActivePath(option.path)
                      ? 'text-red-600 bg-gradient-to-r from-red-50 to-red-100'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      );
    }

    if (item.onClick) {
      return (
        <button
          key={item.label}
          onClick={() => {
            item.onClick();
            setIsMobileOpen(false);
          }}
          className={`w-full flex items-center px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${item.className || 'text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100'
            }`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.className?.includes('text-red-600') ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg' : 'bg-gradient-to-br from-gray-100 to-gray-200'
            }`}>
            <item.icon className={`w-5 h-5 ${item.className?.includes('text-red-600') ? 'text-white' : 'text-gray-600'
              }`} />
          </div>
          <span className="ml-3">{item.label}</span>
        </button>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setIsMobileOpen(false)}
        className={`flex items-center px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${isActivePath(item.path)
            ? 'text-red-600 bg-gradient-to-r from-red-50 to-red-100'
            : 'text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100'
          }`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActivePath(item.path) ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg' : 'bg-gradient-to-br from-gray-100 to-gray-200'
          }`}>
          <item.icon className={`w-5 h-5 ${isActivePath(item.path) ? 'text-white' : 'text-gray-600'
            }`} />
        </div>
        <span className="ml-3">{item.label}</span>
      </Link>
    );
  };

  // Function to create the toggle switch component
  const DashboardToggle = () => {
    const isDonorDashboard = location.pathname.startsWith('/dashboard');
    const hasRequesterRole = user?.roles?.includes('requestor');
    const hasDonorRole = user?.roles?.includes('donor');

    // If user doesn't have both roles, don't show the toggle
    if (!hasRequesterRole || !hasDonorRole) {
      return null;
    }

    const handleToggle = async () => {
      const newRole = isDonorDashboard ? 'requestor' : 'donor';
      handleRoleSwitch(newRole);
    };

    return (
      <motion.div
        className="px-2 py-2 mb-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-2 border border-gray-200 shadow-sm">
          <div className="text-center mb-3">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-1.5">
              <Settings className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-xs font-bold text-gray-800 mb-0.5">Switch Dashboard</p>
            <p className="text-xs text-gray-600">Choose your role</p>
          </div>

          <div className="relative bg-gray-200 rounded-xl p-1">
            <div className="w-full flex items-center justify-between bg-white rounded-lg p-1 relative shadow-sm">
              {/* Donor side */}
              <motion.button
                onClick={isDonorDashboard ? null : handleToggle}
                className={`flex-1 py-1 px-1.5 rounded-md text-center text-[10px] font-semibold z-10 transition-all duration-300 flex items-center justify-center space-x-1 ${isDonorDashboard
                    ? 'text-red-600 bg-gradient-to-r from-red-50 to-red-100 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                whileHover={!isDonorDashboard ? { scale: 1.02 } : {}}
                whileTap={!isDonorDashboard ? { scale: 0.98 } : {}}
              >
                <Heart className={`w-3.5 h-3.5 ${isDonorDashboard ? 'text-red-600' : 'text-gray-400'}`} />
                <span>Donor</span>
              </motion.button>

              {/* Requester side */}
              <motion.button
                onClick={isDonorDashboard ? handleToggle : null}
                className={`flex-1 py-1 px-1.5 rounded-md text-center text-[10px] font-semibold z-10 transition-all duration-300 flex items-center justify-center space-x-1 ${!isDonorDashboard
                    ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                whileHover={isDonorDashboard ? { scale: 1.02 } : {}}
                whileTap={isDonorDashboard ? { scale: 0.98 } : {}}
              >
                <Users className={`w-3.5 h-3.5 ${!isDonorDashboard ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>Requester</span>
              </motion.button>

              {/* Sliding background */}
              <motion.div
                className={`absolute top-1 bottom-1 w-1/2 rounded-md bg-gradient-to-r transition-all duration-300 ${isDonorDashboard
                    ? 'from-red-100 to-red-200 translate-x-0'
                    : 'from-blue-100 to-blue-200 translate-x-full'
                  }`}
                layout
              />
            </div>
          </div>

          <div className="mt-2 text-center">
            <div className="inline-flex items-center space-x-1.5 bg-white rounded-full px-2 py-1 shadow-sm">
              <div className={`w-1.5 h-1.5 rounded-full ${isDonorDashboard ? 'bg-red-500' : 'bg-blue-500'}`}></div>
              <p className="text-[10px] text-gray-600">
                Currently: <span className={`font-bold ${isDonorDashboard ? 'text-red-600' : 'text-blue-600'}`}>
                  {isDonorDashboard ? 'Donor Mode' : 'Requester Mode'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const getDashboardMenuItems = () => {
    const isDonorDashboard = location.pathname.startsWith('/dashboard');
    const userRole = user?.role || 'donor';

    const commonMenuItems = [
      {
        customComponent: DashboardToggle
      },
    ];

    const donorMenuItems = [
      ...commonMenuItems,
      {
        icon: Home,
        label: 'Available Requests',
        path: '/dashboard/available',
        description: 'View nearby blood requests'
      },
      {
        icon: ListChecks,
        label: 'Accepted Requests',
        path: '/dashboard/accepted',
        description: 'Manage your accepted donations'
      },
      {
        icon: History,
        label: 'Donation History',
        path: '/dashboard/history',
        description: 'View your past donations'
      },
      {
        icon: User,
        label: 'Profile Settings',
        path: '/dashboard/profile',
        description: 'Manage your account'
      },
      // Add divider before logout
      { isDivider: true },
      {
        icon: LogOut,
        label: 'Logout',
        onClick: handleLogout,
        description: 'Sign out of your account',
        className: 'text-red-600 hover:bg-red-50'
      }
    ];

    const requesterMenuItems = [
      ...commonMenuItems,
      {
        icon: Plus,
        label: 'Create Request',
        path: '/requester-dashboard/create',
        description: 'Create new blood request'
      },
      {
        icon: ListChecks,
        label: 'Active Requests',
        path: '/requester-dashboard/active',
        description: 'View accepted donors'
      },
      {
        icon: History,
        label: 'Request History',
        path: '/requester-dashboard/history',
        description: 'View past blood requests'
      },
      {
        icon: User,
        label: 'Profile Settings',
        path: '/requester-dashboard/profile',
        description: 'Manage hospital details'
      },
      // Add divider before logout
      { isDivider: true },
      {
        icon: LogOut,
        label: 'Logout',
        onClick: handleLogout,
        description: 'Sign out of your account',
        className: 'text-red-600 hover:bg-red-50'
      }
    ];

    return isDonorDashboard ? donorMenuItems : requesterMenuItems;
  };

  const menuItems = getDashboardMenuItems();

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: sidebarStyles }} />

      {/* Desktop Sidebar - Left Side */}
      <motion.aside
        initial={false}
        animate={{ width: isOpen ? '240px' : '64px' }}
        className="hidden md:block fixed top-0 left-0 h-full bg-white/95 backdrop-blur-md border-r border-gray-200/50 z-20 shadow-xl flex flex-col"
        style={{
          left: 0,
          top: 0,
          height: '100vh',
          zIndex: 20
        }}
      >
        {/* Logo Section - Desktop */}
        <div className={`${isOpen ? 'h-16' : 'h-14'} flex items-center justify-between px-3 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200/50 flex-shrink-0`}>
          <motion.div
            initial={{ width: isOpen ? 180 : 40 }}
            animate={{ width: isOpen ? 180 : 40 }}
            transition={{ duration: 0.3 }}
            className="cursor-pointer"
            onClick={handleLogoClick}
          >
            <img
              src="/images/logo1.png"
              alt="Logo"
              className="h-8 w-auto object-contain"
            />
          </motion.div>
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight
              className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''
                }`}
            />
          </motion.button>
        </div>

        {/* User Info - Desktop */}
        {isOpen && (
          <motion.div
            className="px-3 py-2 border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white mx-2 mt-2 rounded-xl shadow-sm flex-shrink-0"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate drop-shadow-sm">
                  {location.pathname.startsWith('/requester-dashboard')
                    ? (user?.hospitalName || 'Hospital')
                    : (user?.name || 'User Name')
                  }
                </p>
                <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                  <Mail className="w-3 h-3" />
                  <p className="truncate drop-shadow-sm">{user?.email || 'user@example.com'}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${location.pathname.startsWith('/requester-dashboard')
                  ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                  : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                }`}>
                {location.pathname.startsWith('/requester-dashboard') ? 'Requester' : 'Donor'}
              </span>
            </div>
          </motion.div>
        )}

        {/* Navigation - Desktop */}
        <nav className={`flex-1 ${isOpen ? 'px-2 py-3 space-y-1 overflow-y-auto sidebar-scroll' : 'px-1.5 py-2 space-y-1 overflow-hidden'}`}>
          {menuItems.map(renderMenuItem)}
        </nav>
      </motion.aside>

      {/* Main Content - Right Side */}
      <div
        className={`flex-1 flex flex-col min-h-screen w-full transition-all duration-300
          ${isOpen ? 'md:ml-[240px]' : 'md:ml-[64px]'}`}
        style={{
          marginLeft: isOpen ? '240px' : '64px',
          width: `calc(100% - ${isOpen ? '240px' : '64px'})`
        }}
      >
        <div className="flex-1 relative">
          {children}
        </div>
      </div>

      {/* Mobile Menu Button - Top Left */}
      <motion.button
        onClick={() => setIsMobileOpen(true)}
        className="fixed top-4 left-4 z-30 p-3 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl md:hidden hover:bg-gray-50 transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </motion.button>

      {/* Mobile Menu - Left Side Slide */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Mobile Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
            />

            {/* Mobile Sidebar - Left Side */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 h-full w-[280px] bg-white/95 backdrop-blur-md z-50 shadow-2xl md:hidden rounded-r-3xl overflow-hidden flex flex-col"
              style={{
                left: 0,
                top: 0,
                height: '100vh',
                zIndex: 50
              }}
            >
              {/* Mobile Logo Section */}
              <div className="h-24 flex items-center justify-between px-8 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200/50 flex-shrink-0">
                <img
                  src="/images/logo1.png"
                  alt="Logo"
                  className="h-14 w-auto cursor-pointer"
                  onClick={(e) => {
                    handleLogoClick(e);
                    setIsMobileOpen(false);
                  }}
                />
                <motion.button
                  onClick={() => setIsMobileOpen(false)}
                  className="p-2 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </motion.button>
              </div>

              {/* Mobile User Info */}
              <div className="px-8 py-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <User className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-gray-900 truncate drop-shadow-sm">
                      {location.pathname.startsWith('/requester-dashboard')
                        ? (user?.hospitalName || 'Hospital Name')
                        : (user?.name || 'User Name')
                      }
                    </p>
                    <div className="flex items-center space-x-1 text-sm text-gray-500 mt-1">
                      <Mail className="w-3.5 h-3.5" />
                      <p className="truncate drop-shadow-sm">{user?.email || 'user@example.com'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-start">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${location.pathname.startsWith('/requester-dashboard')
                      ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                      : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                    }`}>
                    {location.pathname.startsWith('/requester-dashboard') ? 'Requester' : 'Donor'}
                  </span>
                </div>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 px-6 py-6 overflow-y-auto sidebar-scroll">
                <div className="space-y-3 pb-6">
                  {menuItems.map((item, index) => {
                    if (item.isDivider) {
                      return <div key={`divider-${index}`} className="my-4 border-t border-gray-200" />;
                    }

                    return renderMobileMenuItem(item);
                  })}
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Role Switch Confirmation Modal */}
      <AnimatePresence>
        {showRoleConfirm && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={cancelRoleSwitch}
              className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden w-full max-w-md max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-500 to-red-600 p-4 sm:p-6 text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Switch Role</h3>
                  <p className="text-red-100 text-sm sm:text-base">Confirm your role change</p>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                  <div className="text-center mb-4 sm:mb-6">
                    <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
                      Are you sure you want to switch to{' '}
                      <span className={`font-bold ${pendingRole === 'requestor' ? 'text-blue-600' : 'text-red-600'}`}>
                        {pendingRole === 'requestor' ? 'Requester' : 'Donor'}
                      </span>{' '}
                      mode?
                    </p>
                    <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-500">
                      <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${pendingRole === 'requestor' ? 'bg-blue-500' : 'bg-red-500'}`}></div>
                      <span>This will change your dashboard view and available features</span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                    <motion.button
                      onClick={cancelRoleSwitch}
                      className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200 text-sm sm:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={confirmRoleSwitch}
                      className={`flex-1 py-3 px-4 text-white font-semibold rounded-xl transition-all duration-200 text-sm sm:text-base ${pendingRole === 'requestor'
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                          : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                        }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Confirm Switch
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardLayout;